import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Button,
  Box,
  Typography,
  Autocomplete,
  TextField,
  CircularProgress
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { 
  addLostScooterAsync, 
  getLostScootersAsync, 
  getAllScootersAsync,
  selectLostScooters,
  Scooter
} from '../../redux/slices/lostScootersSlice';
import { selectAdmins } from '../../redux/slices/adminsSlice';

interface Props {
  open: boolean;
  onClose: () => void;
}

const AddLostScooterModal = ({ open, onClose }: Props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { allScooters, scootersLoading } = useSelector(selectLostScooters);
  const { currentCity } = useSelector(selectAdmins);
  const [selectedScooter, setSelectedScooter] = useState<Scooter | null>(null);
  const [loading, setLoading] = useState(false);

  // Filter scooters by current city
  const filteredScooters = useMemo(() => {
    if (!currentCity) return allScooters;
    return allScooters.filter((scooter: any) => {
      const cityId = typeof scooter.city === 'string' ? scooter.city : scooter.city?._id;
      return cityId === currentCity;
    });
  }, [allScooters, currentCity]);

  useEffect(() => {
    if (open) {
      dispatch(getAllScootersAsync() as any);
    }
  }, [open, dispatch]);

  useEffect(() => {
    if (!open) {
      setSelectedScooter(null);
      setLoading(false);
    }
  }, [open]);

  const handleClose = () => {
    setSelectedScooter(null);
    setLoading(false);
    onClose();
  };

  const handleSubmit = () => {
    if (!selectedScooter) {
      alert(t('lostScooters.selectScooterAlert'));
      return;
    }

    setLoading(true);
    dispatch(
      addLostScooterAsync(
        selectedScooter._id,
        () => {
          setTimeout(() => {
            dispatch(getLostScootersAsync() as any);
          }, 500);
          handleClose();
        },
        () => {
          setLoading(false);
        }
      ) as any
    );
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px'
        }
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" fontWeight={700}>
          {t('lostScooters.modalTitle')}
        </Typography>
        <IconButton onClick={handleClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ pt: 2 }}>
          <Autocomplete
            fullWidth
            options={filteredScooters}
            getOptionLabel={(option) => `${option.name} (ID: ${option._id})`}
            value={selectedScooter}
            onChange={(_, newValue) => setSelectedScooter(newValue)}
            loading={scootersLoading}
            disabled={loading}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('lostScooters.scooterLabel')}
                placeholder={t('lostScooters.scooterPlaceholder')}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {scootersLoading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  )
                }}
              />
            )}
            filterOptions={(options, { inputValue }) => {
              const searchTerm = inputValue.toLowerCase();
              return options.filter(
                (option) =>
                  option.name.toLowerCase().includes(searchTerm) ||
                  option._id.toLowerCase().includes(searchTerm)
              );
            }}
            noOptionsText={currentCity ? t('lostScooters.noOptionsCity') : t('lostScooters.noOptions')}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ padding: '16px 24px' }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          sx={{
            borderRadius: '8px',
            textTransform: 'none',
            borderColor: '#878787',
            color: '#878787'
          }}
          disabled={loading}
        >
          {t('lostScooters.cancelBtn')}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          sx={{
            borderRadius: '8px',
            textTransform: 'none',
            backgroundColor: '#5073F8',
            '&:hover': {
              backgroundColor: '#3D5FD9'
            }
          }}
          disabled={loading}
        >
          {loading ? t('lostScooters.adding') : t('lostScooters.addBtnShort')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddLostScooterModal;
